/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

export const enum FeatureFlag {
  // (Permanent) Enables access control checks in Cloud, but not in CP4D.
  ACCESS_CONTROL = 'access-control',

  ACTIONS_AUTOLEARN_NEW_IA = 'actions-autolearn-new-ia',
  ACTIONS_CLIENT_ACTIONS = 'actions-client-actions',
  ACTIONS_GENERATIVE_AI = 'actions-generative-ai',
  ACTIONS_GENERATIVE_AI_BASE_LLM = 'actions-generative-ai-base-llm',
  ACTIONS_GENERATIVE_AI_COLLECT_VERBATIM = 'actions-generative-ai-collect-verbatim',
  ACTIONS_GENERATIVE_AI_HAP_FILTERING = 'actions-generative-ai-hap-filtering',
  // Default false, used to enable slot filling feature in more languages than just english. Currently enabled only for
  // SAP on cp4d. ACTIONS_GENERATIVE_AI will also need to be enabled for this flag to have any effect.
  ACTIONS_GENERATIVE_AI_SLOT_FILLING_MULTILINGUAL = 'actions-generative-ai-slot-filling-multilingual',
  ACTIONS_IMPORT_ACTIONS = 'actions-import-actions',
  ACTIONS_INTENT_RENAME = 'actions-intent-rename',

  ACTIONS_NEW_IA_AI_GUIDED = 'actions-new-ia-ai-guided',
  // Still being tried out until it gains enough adoption.
  ACTIONS_NEW_IA_BOOST_WORDS = 'actions-new-ia-boost-words',

  ACTIONS_NEW_IA_LAUNCH_MODES = 'actions-new-ia-launch-modes',

  ACTIONS_NEW_IA_SEARCH_ROUTING = 'actions-new-ia-search-routing',
  ACTIONS_SHOW_CREATE_ACTION_PANEL_NON_ENGLISH = 'actions-show-create-action-panel-non-english',

  ACTIONS_SYNONYM_RECOMMENDATIONS = 'actions-synonyms-recommendations',
  // Enabled in Cloud but disabled in CP4D.
  ANALYTICS_UNRECOGNIZED = 'analytics-unrecognized',

  // (Permanent) Enables premium features for some customers (enabled at the Store API level).
  BETA = 'beta',

  CHANNEL_TRANSFER = 'channel-transfer',

  // Enabled only for some customers in production.
  CHANNELS_SEGMENT_NON_ENTERPRISE = 'channels-segment-non-enterprise',

  // Flag to release new channels SMS provider Genesys. Should not be enabled on Classic IA and CP4D
  CHANNELS_SMS_PROVIDER_GENESYS = 'channels-sms-provider-genesys',

  // Flag to release new channels SMS provider Genesys. Should not be enabled on Classic IA and CP4D
  CHANNELS_SMS_PROVIDER_CUSTOM = 'channels-sms-provider-custom',

  // (Temporary) Enabled when the server is running CPD at an older version (4.x)
  // where some features must be disabled.
  // After CPD 4.8.9 is deployed by 2Q 2025, this flag should be removed.
  CPD_VERSION_4 = 'cpd-version-4',

  // (Permanent) Enables the in-Tooling help webchat (Cristal) in Cloud,
  // but disables it in CP4D.
  CRISTAL_SUPPORT = 'cristal-support',

  DIALOG_NEW_IA_INTENT_CONFLICTS = 'dialog-new-ia-intent-conflicts',
  DIALOG_NEW_IA_SYNONYM_RECOMMENDATIONS = 'dialog-new-ia-synonym-recommendations',

  // Default false, controls whether or not the conversational search feature / toggle is visible during search setup.
  // Currently used to opt users in to the conversational search beta.
  DISCOVERY_LLM_CONVERSATIONAL_SEARCH = 'discovery-llm-conversational-search',
  // Default false, used to enable conversational search feature in more languages than just english. Currently enabled
  // only for SAP on cp4d. DISCOVERY_LLM_CONVERSATIONAL_SEARCH will also need to be enabled for this flag to have any
  // effect.
  DISCOVERY_LLM_CONVERSATIONAL_SEARCH_MULTILINGUAL = 'discovery-llm-conversational-search-multilingual',

  // Default true, controls whether or not the discovery option is available when configuring a search skill.
  // Currently used to disable Discovery in the AWS platform.
  DISCOVERY_SEARCH = 'discovery-search',

  // Enables elastic document uploads: currently enabled in Cloud but not CP4D.
  ELASTIC_SEARCH_DOCUMENT_UPLOAD = 'elastic-search-document-upload',

  // Enabled in production for a few customers.
  ENABLE_LOG_MESSAGE_WEBHOOK = 'enable-log-message-webhook',

  // (Permanent) Enabled in CP4D to give their users premium features,
  // even though CP4D instances use the Standard plan.
  ENABLE_PREMIUM_FEATURES = 'enable-premium-features',

  ENHANCED_ON_TOPIC_CLASSIFIER_DEFAULT = 'enhanced-on-topic-classifier-default',

  // (Permanent) Enables Hotjar tracking only in select production environments.
  HOTJAR_TRACKING = 'hotjar-tracking',

  IA_AB_TEST_NEW_INSTANCE = 'ia-ab-test-new-instance',
  IA_ACCESS_UPDATES = 'ia-access-updates',

  // Controls whether or not the multi turn toggle is available for conversational search.
  IA_CONVERSATIONAL_SEARCH_MULTI_TURN = 'ia-conversational-search-multi-turn',

  // (Permanent) Enables New IA as a feature in the product in Cloud and CP4D,
  // except Cloud instances using the Standard plan.
  IA_NEW_IA_ENABLED = 'ia-new-ia-enabled',

  // Inline notification banner for the Home, Actions and Intents pages
  IA_ONGOING_MAINTENANCE = 'ia-ongoing-maintenance',
  IA_UPCOMING_MAINTENANCE = 'ia-upcoming-maintenance',

  // By default, WXA under WXO Agent Builder will hide several features related
  // to it (e.g. skill-based and AI-guided actions, etc); however, if we want
  // to show said features even under Agent Builder, this flag can be enabled.
  IA_WXO_AGENT_BUILDER_FEATURES = 'ia-wxo-agent-builder-features',

  // If enabled, users will be able to enable Classic IA within WXO.
  IA_WXO_CLASSIC_SUPPORTED = 'ia-wxo-classic-supported',

  // If enabled, users will be able to enable Dialog within WXO.
  IA_WXO_DIALOG_SUPPORTED = 'ia-wxo-dialog-supported',

  INTEGRATIONS = 'integrations',

  // Enables custom extensions update: currently enabled in Cloud but not CP4D.
  INTEGRATIONS_CUSTOM_UPDATE = 'integrations-custom-update',

  INTENT_CONFLICTS = 'intent-conflicts',

  // Enabled in Cloud but disabled in CP4D.
  INTERCOM_INTEGRATION = 'intercom-integration',

  LLM_LET_ASSISTANT_DECIDE = 'llm-let-assistant-decide',

  OFF_TOPIC_V2 = 'off-topic-v2',
  OPEN_ENTITIES = 'open-entities',
  PHONE = 'channels-phone-enabled',
  PHONE_AWS = 'channels-phone-aws-enabled',
  PHONE_AMAZON_CONNECT = 'channels-phone-amazon-connect',
  PHONE_SOE_SUPPORT = 'phone-soe-support',
  PHONE_AUDIO_ATTESTATION = 'channels-phone-audio-attestation',
  // Enabled ONLY in CPD and not in Cloud
  PHONE_GENESYS_AUDIO_CONNECTOR = 'channels-phone-genesys-audio-connector',
  // Enabled ONLY in Cloud & for certain customers only (currently Elevance health)
  PHONE_GENESYS_AUDIO_CONNECTOR_ELEVENLABS = 'channels-phone-genesys-audio-connector-elevenlabs',

  // (Permanent) Default false, used by SAP to receive a text response type for conversational search instead of the
  // default conversational_search response type. DISCOVERY_LLM_CONVERSATIONAL_SEARCH will also need to be enabled for
  // this flag to have any effect.
  SAP_CONVERSATIONAL_SEARCH = 'sap-conversational-search',

  // A workspaces + dialog specific feature flag that has variable availability depending on the skill language.
  SEARCH = 'search',
  SEARCH_SKILL_HIGHLIGHT_ANSWERS = 'search-skill-highlight-answers',

  SKILL_VERSIONS = 'skill-versions',
  SPELL_CHECK = 'spell-check',
  SPELL_CHECK_DEFAULT = 'spell-check-default',
  SYNONYM_RECOMMENDATIONS = 'synonym-recommendations',

  // (Permanent): Shows transaction IDs in error toast messages in development
  // and staging environments, but hides it from production.
  TRANSACTION_IDS = 'transaction-ids',

  TWILIO_TEXT_INTEGRATION = 'twilio-text-integration',
  WEB_EXPERIENCE = 'web-experience',
  WEB_EXPERIENCE_SALESFORCE = 'web-experience-salesforce',
  WEB_EXPERIENCE_ZENDESKWEB = 'web-experience-zendeskweb',
  WHATSAPP_INTEGRATION = 'whatsapp-integration'
}

export type FeatureFlags = Record<FeatureFlag, boolean>;

export type LanguageFeatures = {
  label: string;
  value: string;
  experimental: boolean;
  [FeatureFlag.ENHANCED_ON_TOPIC_CLASSIFIER_DEFAULT]?: boolean;
  [FeatureFlag.OFF_TOPIC_V2]?: boolean;
  [FeatureFlag.OPEN_ENTITIES]?: boolean;
  [FeatureFlag.SEARCH]: boolean;
  [FeatureFlag.SPELL_CHECK]?: boolean;
  [FeatureFlag.SPELL_CHECK_DEFAULT]?: boolean;
  [FeatureFlag.SYNONYM_RECOMMENDATIONS]?: boolean;
};
